import { Box, Button, DialogProps, Grid } from "@mui/material";
import moment from "moment";
import { useMemo } from "react";
import { useForm } from "react-hook-form";

import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import useCases from "../../../../hooks/useCases";
import usePayment from "../../../../hooks/usePayment";
import { useSnackbarHelper } from "../../../components/Snackbar";
import BasicDatePicker from "../../../components/inputs/BasicDatePicker";
import MuiTextField from "../../../components/inputs/MuiTextField";
import NumberField from "../../../components/inputs/NumberField";
import Select from "../../../components/inputs/Select";
import BasicModal from "../../../components/modal/BasicModal";

interface Props extends DialogProps {
  onClose: () => void;
  caseId?: any;
  data?: any;
  contactName?: string;
  payment_spread?: any;
  clientEmail?: string;
  clientZip?: string;
  refetchCasePaymentScheduling: () => void;

  // refetchPayment: () => void;
}

function AddPaymentMadeModal({
  onClose,
  caseId,
  data,
  refetchCasePaymentScheduling,
  contactName,
  clientEmail,
  clientZip,
  payment_spread,
  // refetchPayment,
  ...props
}: Props) {
  const { selfPaymentMade, isselfPaymentMade } = usePayment(caseId?.id);

  const { refetchCase } = useCases(caseId?.id);

  const showSnackbar = useSnackbarHelper();

  const initialValues = useMemo(
    () => ({
      paymentMadeDate: moment(Date.now()),
      paidBy: contactName || "",
      type: data?.type || "",
      amount: "",
      notes: "",
      chequeNumber: null,
    }),
    [data, contactName]
  );
  const validationSchema = Yup.object().shape({
    // dueDate: Yup.date().required("Required"),
    paymentMadeDate: Yup.date().required("Required"),
    paidBy: Yup.string().required("Required"),
    type: Yup.string().required("Required"),
    // amount: Yup.string().required("Required"),
    // amount: Yup.number()
    //   .required("Required")
    //   .min(0.01, "Amount must be at least $1"),
    amount: Yup.string()
      .required("Required")
      .test("min-amount", "Amount must be at least $1", (value) => {
        const num = parseFloat(value);
        return !isNaN(num) && num >= 1;
      }),
    notes: Yup.string(),
    chequeNumber: Yup.string().when("type", (type, schema) => {
      return type?.[0] === "cheque"
        ? schema.required("Required")
        : schema.notRequired().nullable();
    }),
  });

  const formContext = useForm({
    defaultValues: initialValues,
    resolver: yupResolver<any>(validationSchema),
  });
  const { watch } = formContext;
  const type = watch("type");

  const handleSubmit = async (values?: any) => {
    const amount = (data?.feeQuoted || 0) - (data?.totalPaidAmount || 0);
    const pendingAmount = Math.max(amount, 0);

    // Round both values to 2 decimal places for a reliable comparison
    const fieldAmount = Number(values?.amount);
    const roundedPendingAmount = Number(pendingAmount.toFixed(2));
    const roundedEnteredAmount = Number(fieldAmount?.toFixed(2));

    if (roundedEnteredAmount > roundedPendingAmount) {
      const message = "Entered amount is more than the available balance.";
      showSnackbar(message, "error");
      return null;
    }

    try {
      const paymentScheduling = {
        isPaid: true,
        schedulingId: data?._id,
        date: values?.dueDate || "",
        paymentMadeDate: values?.paymentMadeDate || Date.now(),
        paidBy: values?.paidBy || "",
        type: values?.type || "",
        amount: values?.amount || "",
        notes: values?.notes || "",
        feeQuoted: data?.feeQuoted || "",
        totalPaidAmount: data?.totalPaidAmount || 0,
        customerId: data?.customerId,
        journalEntryId: data?.journalEntryId,
        chequeNumber: values?.chequeNumber || null,
        clientEmail: clientEmail,
        clientName: contactName,
        clientZip: clientZip,
        isManuallyAddedInstallment: true,
      };

      const response = await selfPaymentMade({
        schedulingId: data?._id,
        payload: paymentScheduling,
      });

      // const response = await deleteScheduling(id);
      if (response?.status === 200) {
        const message = response?.data?.message || "Payment added successfully";
        showSnackbar(message, "success");
      }
      onClose();
      // refetchPayment();
      refetchCasePaymentScheduling();
      refetchCase();
    } catch (error: any) {
      const message = error?.response?.data?.message || "An error occurred";
      showSnackbar(message, "error");
      // onClose();
    }
  };

  return (
    <BasicModal
      formContext={formContext}
      onClose={onClose}
      onSubmit={handleSubmit}
      isLoader={isselfPaymentMade}
      loadingTitle="Loading..."
      title={"Payment Made"}
      content={
        <Box>
          <Grid
            container
            spacing={2}
            sx={{
              "& .outlined .MuiOutlinedInput-notchedOutline": {
                border: "2px solid #AFD2F0 !important",
              },
            }}
          >
            <Grid item sm={6}>
              <BasicDatePicker
                label="Payment made Date"
                name="paymentMadeDate"
                required
              />
            </Grid>
            <Grid item sm={6}>
              <MuiTextField fullWidth label="Paid By" name="paidBy" required />
            </Grid>
            <Grid item sm={6}>
              <Select
                label="Type"
                // disabled
                name="type"
                options={[
                  { label: "Credit Card", value: "creditcard" },
                  { label: "Check", value: "cheque" },
                  { label: "Cash", value: "cash" },
                  { label: "Money Order", value: "moneyOrder" },
                  { label: "Other", value: "other" },
                ]}
                required
                fullWidth
              />
            </Grid>
            <Grid item sm={6}>
              <NumberField
                fullWidth
                label="Amount"
                name="amount"
                required
                // disabled={isLastSpread}
                // fixedDecimalScale
                prefix="$ "
                // fixedDecimalScale={true}
                max={999999999}
              />
            </Grid>
            {type === "cheque" && (
              <Grid item sm={6}>
                <MuiTextField
                  fullWidth
                  label="Check Number"
                  name="chequeNumber"
                  required={type === "cheque" ? true : false}
                />
              </Grid>
            )}
            <Grid item sm={12}>
              <MuiTextField
                name="notes"
                label="Details"
                fullWidth
                rows={2}
                multiline
              />
            </Grid>
          </Grid>
        </Box>
      }
      actions={
        <>
          <Button
            color="error"
            variant="outlined"
            onClick={onClose}
            disabled={isselfPaymentMade}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            sx={{ textTransform: "capitalize" }}
            type="submit"
            disabled={isselfPaymentMade}
          >
            Mark as Paid
          </Button>
        </>
      }
      {...props}
    />
  );
}

export default AddPaymentMadeModal;
